import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid';

import { StatusChip } from '@/components/Cards/Chips/StatusChip';
import { formatDate } from '@/utils/common';

import {
  getStatusOrderBg,
  getStatusOrderColor,
  getStatusOrderLabel,
  OrderStatus,
} from './constants';

interface ColumnProps {
  onCreateOrder: (id: string) => void;
  creatingOrderIds: Set<string>;
}

const parseErrorMessage = (response: string | undefined): string | null => {
  if (!response) return null;
  try {
    const parsed = JSON.parse(response) as { message?: string };
    return parsed.message ?? null;
  } catch {
    return null;
  }
};

export const fulfillOrdersColumns = ({
  onCreateOrder,
  creatingOrderIds,
}: ColumnProps): GridColDef[] => [
  {
    field: 'orderId',
    flex: 1,
    headerName: 'N° de Venta Mercado Libre',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: ({ row }) => {
      const packId = row.packId;
      const hasPackId = typeof packId === 'string' && packId.trim() !== '';

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
          gap={0.5}
        >
          <Typography variant="body2" display="inline" sx={{ fontWeight: 'bold' }}>
            {row.orderId}
          </Typography>
          {hasPackId && (
            <Chip
              icon={<Inventory2OutlinedIcon fontSize="small" />}
              label={`Paquete: ${packId}`}
              size="small"
            />
          )}
        </Box>
      );
    },
  },
  {
    field: 'createdAt',
    flex: 1,
    headerName: 'Fecha de pedido',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: ({ value }) => (
      <Typography
        variant="body2"
        display="inline"
        sx={{ color: (theme) => theme.palette.grey[600] }}
      >
        {formatDate(value, 'dd/MM/yyyy')}
      </Typography>
    ),
  },
  {
    field: 'statusOrder',
    flex: 1,
    headerName: 'Estado',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: ({ row }) => (
      <Box>
        <StatusChip
          status={getStatusOrderLabel(row.statusOrder)}
          color={getStatusOrderColor(row.statusOrder)}
          bg={getStatusOrderBg(row.statusOrder)}
        />
      </Box>
    ),
  },
  {
    field: 'trackingId',
    flex: 1,
    headerName: 'Nº de seguimiento',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: ({ row }) => {
      if (row.trackingId?.length > 0) {
        return (
          <Typography
            variant="body2"
            display="inline"
            sx={{ fontWeight: 'bold', color: (theme) => theme.palette.primary.main }}
          >
            {row.trackingId}
          </Typography>
        );
      }

      if (row.status === OrderStatus.ERROR) {
        const errorMsg = parseErrorMessage(row.response);
        return (
          <Tooltip followCursor title={errorMsg ?? 'Error al procesar'}>
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <ErrorOutlineIcon fontSize="small" color="error" />
            </Box>
          </Tooltip>
        );
      }

      return (
        <Typography
          variant="body2"
          display="inline"
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          -
        </Typography>
      );
    },
  },
  {
    field: 'logisticMode',
    flex: 1,
    headerName: 'Modo / Tipo logístico',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: ({ row }) => {
      const mode = row.orderData?.logisticMode || '-';
      const type = row.orderData?.logisticType || '-';
      return (
        <Typography
          variant="body2"
          display="inline"
          sx={{ color: (theme) => theme.palette.grey[600] }}
        >
          {mode} / {type}
        </Typography>
      );
    },
  },
  {
    field: 'actions',
    flex: 0.5,
    headerName: 'Acciones',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    resizable: false,
    renderCell: ({ row }) => {
      const isCreating = creatingOrderIds.has(row.id);
      const isCreated = row.status === 'created';
      const canCreate = !isCreated;
      return (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
        >
          {isCreating ? (
            <CircularProgress size={20} />
          ) : (
            <Tooltip
              followCursor
              title={isCreated ? 'Orden ya generada' : 'Crear orden en 99minutos Fulfill'}
              placement="top"
            >
              <span>
                <IconButton
                  color="primary"
                  size="small"
                  disabled={!canCreate}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateOrder(row.id);
                  }}
                >
                  <AddCircleOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    },
  },
];
